import { useEffect, useContext, useRef } from 'react';

import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { Language } from '@lessonPlanner/enums';

import { COOKIE_NAMES } from '../constants/cookies';
import PortalContext from '../context/portal/context';
import {
  LessonPlanProperties,
  ObjectiveProperties,
  QuizMakerSingleProperties,
  SubjectExtensionsProperties,
  DllProperties,
  ActivitiesSingleProperties,
  AnalogyContextualizerProperties,
  CatchUpFridaysProperties,
  SummativeTestProperties,
  CerebroLessonPlanProperties,
} from '../enums/pageKey';
import { Portal, PortalLink } from '../enums/portal.enum';
import { curriculumSpecificPaths } from '../utils/portalRedirection';

import useLocalStorageItem from './useLocalStorageItem';

export const usePortalManagement = (
  country: string | undefined,
  isPublicPage?: boolean,
) => {
  const { selectedPortal, setSelectedPortal } = useContext(PortalContext);
  const { setItemByKey } = useLocalStorageItem();
  const previousPortal = useRef<Portal | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (selectedPortal) return;

    const storedPortal = Cookies.get('selectedPortal') as Portal;
    if (storedPortal) {
      setSelectedPortal(storedPortal);
      return;
    }

    if (country) {
      const newPortal =
        country === 'PH' ? Portal.PHILIPPINES : Portal.INTERNATIONAL;
      setSelectedPortal(newPortal);
      Cookies.set(COOKIE_NAMES.SELECTED_PORTAL, newPortal, { expires: 365 });
    }
  }, [country, selectedPortal, setSelectedPortal]);

  useEffect(() => {
    if (isPublicPage || !selectedPortal) return;

    const currentPath = router.asPath;
    const isAlreadyOnPortalPath = Object.values(PortalLink).some(
      (portalPath) =>
        currentPath.startsWith(portalPath) && currentPath !== portalPath,
    );

    if (isAlreadyOnPortalPath) return;

    if (router.pathname.includes('[portal]')) return;

    const basePath = router.pathname;

    if (['/'].includes(basePath)) return;

    const isCurriculumSpecific = curriculumSpecificPaths.some(
      (path) => basePath === path || basePath.startsWith(path),
    );

    const targetPortal = isCurriculumSpecific
      ? Portal.PHILIPPINES
      : selectedPortal;
    const portalPath = PortalLink[targetPortal];

    const query = router.query;
    delete query.portal;

    const queryString = new URLSearchParams(
      query as Record<string, string>,
    ).toString();
    const redirectTo = `${portalPath}${basePath}${
      queryString ? `?${queryString}` : ''
    }`;

    router.replace(redirectTo);
  }, [selectedPortal, router, isPublicPage]);

  useEffect(() => {
    if (!selectedPortal || selectedPortal === previousPortal.current) return;

    const languageKeys = [
      LessonPlanProperties.language,
      ObjectiveProperties.language,
      QuizMakerSingleProperties.language,
      SubjectExtensionsProperties.language,
      DllProperties.language,
      ActivitiesSingleProperties.language,
      AnalogyContextualizerProperties.language,
      CatchUpFridaysProperties.language,
      SummativeTestProperties.language,
      CerebroLessonPlanProperties.language,
    ];

    languageKeys.forEach((key) => {
      setItemByKey(key, Language.ENGLISH);
    });

    previousPortal.current = selectedPortal;
  }, [selectedPortal, setItemByKey]);

  return { selectedPortal };
};
